import { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useWorkspaceStore } from '../modules/vendor/side-bar/workspace.store';
import { effectiveWorkspace, workspaceById } from '../modules/vendor/side-bar/workspaces';
import { AuthContext } from '../providers/AuthProvider';

/**
 * Send an old address to its new one, keeping everything it carried.
 *
 * The backoffice's addresses were reorganised by workspace — /market/search is
 * now /business/visibility/search — but the old ones live on in bookmarks,
 * notification emails, Stripe's return URL and OAuth callbacks registered with
 * Meta. A plain <Navigate> would drop the query string, and with it Stripe's
 * `?checkout=success` or an OAuth `?connected=`. This merges the target's own
 * query (typically `?tab=`) with whatever the old address carried, the
 * incoming values winning for anything but `tab`.
 */
export const LegacyRedirect = ({ to }: { to: string }) => {
    const location = useLocation();
    const [path, targetQuery = ''] = to.split('?');

    const merged = new URLSearchParams(location.search);
    new URLSearchParams(targetQuery).forEach((value, key) => {
        // The target decides which tab an old path meant; everything else the
        // old link carried is passed through.
        if (key === 'tab' || !merged.has(key)) merged.set(key, value);
    });

    const search = merged.toString();
    return <Navigate to={`${path}${search ? `?${search}` : ''}${location.hash}`} replace />;
};

/**
 * Where a vendor lands: the workspace they were last in.
 *
 * Sign-in and the bare site address both arrive at /dashboard, which is shared
 * with super admins and so cannot itself move. For a vendor it is only a door:
 * someone who works in Ads all day signs in to Ads, not to a Home they then
 * have to leave. The choice is the one the switcher remembered in this
 * browser; a first visit has none and goes Home.
 *
 * Read from the store's current value rather than subscribed to, so landing
 * cannot be re-triggered by the rail updating afterwards.
 */
export const LastWorkspaceRedirect = () => {
    const workspace = useWorkspaceStore.getState().workspace;
    // A manufacturer's account lands on the Brand Portal unless it was last in one of its own workspaces.
    const kind = (useContext(AuthContext)?.user as any)?.vendor?.kind;
    return <LegacyRedirect to={workspaceById(effectiveWorkspace(workspace, kind)).home} />;
};

/**
 * Every address that moved, and where it went.
 *
 * Kept as data, beside the component, so the router can register them in one
 * loop and a test can check each target is a real route.
 */
export const LEGACY_REDIRECTS: Array<{ from: string; to: string }> = [
    // Home
    { from: 'subscription/plan', to: '/home/billing/plan' },
    { from: 'subscription/upgrade', to: '/home/billing/upgrade' },
    { from: 'subscription/invoices', to: '/home/billing/plan' },

    // Visualizer
    { from: 'overview', to: '/visualizer/dashboard' },
    { from: 'engagement/leads', to: '/visualizer/engagement/leads' },
    { from: 'engagement/analytics', to: '/visualizer/engagement/analytics?tab=overview' },
    { from: 'engagement/analytics/products', to: '/visualizer/engagement/analytics?tab=products' },
    { from: 'engagement/timeline', to: '/visualizer/engagement/activity' },
    { from: 'configuration', to: '/visualizer/settings?tab=general' },
    { from: 'configuration/general', to: '/visualizer/settings?tab=general' },
    { from: 'configuration/advanced', to: '/visualizer/settings?tab=advanced' },
    { from: 'configuration/product-display', to: '/visualizer/settings?tab=product-display' },
    { from: 'configuration/spaces', to: '/visualizer/spaces' },
    { from: 'configuration/integration', to: '/home/integration' },
    { from: 'configuration/design', to: '/visualizer/settings?tab=general' },
    { from: 'settings', to: '/visualizer/settings?tab=general' },
    { from: 'settings/general', to: '/visualizer/settings?tab=general' },
    { from: 'settings/advanced', to: '/visualizer/settings?tab=advanced' },
    { from: 'products/spaces/my-selection', to: '/visualizer/spaces' },

    // Shopping Assistant
    { from: 'configuration/chat', to: '/assistant/settings' },

    // Marketing
    // /marketing/email/settings itself stays: it is the page, and ?tab= picks templates or SMTP.
    { from: 'marketing/email/settings/smtp', to: '/marketing/email/settings?tab=smtp' },

    // Business
    { from: 'market', to: '/business/dashboard' },
    { from: 'market/overview', to: '/business/dashboard' },
    { from: 'market/search', to: '/business/visibility/search' },
    { from: 'market/profile', to: '/business/visibility/profile' },
    { from: 'market/backlinks', to: '/business/visibility/backlinks' },
    { from: 'market/health', to: '/business/visibility/health' },
    { from: 'market/competitors', to: '/business/competitors/rivals' },
    { from: 'market/gaps', to: '/business/competitors/gaps' },
    { from: 'sales/revenue', to: '/business/sales/revenue' },
    { from: 'sales/shop', to: '/business/sales/shop' },

    // Each workspace's overview became its Dashboard.
    { from: 'visualizer/overview', to: '/visualizer/dashboard' },
    { from: 'assistant/overview', to: '/assistant/dashboard' },
    { from: 'marketing/overview', to: '/marketing/dashboard' },
    { from: 'ads/overview', to: '/ads/dashboard' },
    { from: 'business/overview', to: '/business/dashboard' },

    // Products
    { from: 'products/synch', to: '/products/sync' },
];
